import axios from 'axios';
import Vue from 'vue';
import VueRouter from 'vue-router';
import { ACCOUNTS_OAUTH_CALLBACK_URL } from '@/consts';
import { getToken } from './services/token';

import { getQueryParam, redirectToTarget } from '@/utils/url';
import { AccountsService } from '@/services/accounts';
import { delayInSeconds } from './utils/async';

const orderCrumb = (vertical, orderNumber) => ({
  label: `Pedido #${orderNumber}`,
  to: { name: 'pedido-detalhes', params: { vertical, orderNumber } },
});

/**
 * Valida a sessão sem passar pelo interceptor do AccountsService
 * (que redireciona pra logout em 401, perdendo o target).
 * Retorna:
 * - 'empty' se não houver token de sessão,
 * - 'valid' se a sessão é válida,
 * - 'invalid' se a sessão é inválida (401),
 * - 'error' em qualquer outro erro, após 3 tentativas.
 */
async function validateSession({ scope, attempt = 1 } = {}) {
  if (!getToken()) {
    return 'empty';
  }
  try {
    const requesterId = `perfil/${scope || 'routing'}`;
    await axios.get(Vue.env.VUE_APP_ACCOUNTS_URL + '/auth/session/me/validate', {
      withCredentials: true,
      headers: { 'x-requester-id': requesterId },
      timeout: 30000,
    });
    return 'valid';
  } catch (e) {
    const status = String(e?.response?.status || '');
    if (status === '401') return 'invalid';
    const maxAttempts = 3;
    if (attempt < maxAttempts) {
      await delayInSeconds(2 * attempt);
      return validateSession({ scope, attempt: attempt + 1 });
    }
    return status || 'error';
  }
}

const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location, onResolve, onReject) {
  if (onResolve || onReject) return originalPush.call(this, location, onResolve, onReject);
  return originalPush.call(this, location).catch(err => {
    console.warn(err);
  });
};

Vue.use(VueRouter);

const onlyWithoutTokenGuard = (to, from, next) => {
  if (getToken()) next({ name: 'meu-perfil' });
  else next();
};

const routes = [
  // Área logada
  {
    path: '/',
    beforeEnter: async (to, from, next) => {
      const sessionState = await validateSession({ scope: to.name });
      // 'invalid', 'error' ou status codes — sessão não confirmada, manda pro logout
      if (sessionState !== 'valid' && sessionState !== 'empty') {
        return next({ name: 'logout', query: to.query });
      }

      const oauthState = getQueryParam('state');
      const token = getToken();

      if (token && oauthState) {
        try {
          const accountService = new AccountsService(Vue.env.VUE_APP_ACCOUNTS_URL, 'perfil', undefined);
          const user = await accountService.getLoggedUserOauthCallbackURL(oauthState);
          localStorage.setItem(ACCOUNTS_OAUTH_CALLBACK_URL, user.oauth_callback_url);
          return next({ name: 'oauth-callback-redirect' });
        } catch (error) {
          console.error('Erro Realizando oauthflow', error);
        }
      }

      if (!token) return next({ name: 'login', query: to.query });

      // sessionState === 'valid' garantido aqui.
      // Edge case: usuário logado acessando "/?target=X" diretamente — honrar o target.
      const target = to.query.target;
      if (target) {
        return redirectToTarget({
          target,
          allowedHosts: Vue.env.VUE_APP_ALLOWED_TARGET_HOSTS.split(','),
          fallback: () => next(),
        });
      }

      next();
    },
    component: () => import('./layouts/profile'),
    children: [
      {
        path: '',
        redirect: { name: 'area-do-aluno' },
      },
      {
        path: 'area-do-aluno',
        meta: { breadcrumb: { label: 'Área do aluno', to: { name: 'area-do-aluno' } } },
        name: 'area-do-aluno',
        component: () => import('./pages/profile/area-do-aluno'),
      },
      {
        path: 'meu-perfil',
        meta: { breadcrumb: { label: 'Meu Perfil', to: { name: 'meu-perfil' } } },
        component: () => import('./pages/profile'),
        children: [
          {
            path: '',
            name: 'meu-perfil',
            component: () => import('./pages/profile/meu-perfil'),
          },
          {
            path: 'alterar-perfil',
            name: 'alterar-perfil',
            meta: { breadcrumb: { label: 'Alterar meu perfil' } },
            component: () => import('./pages/profile/meu-perfil/alterar-perfil'),
          },
        ],
      },
      {
        path: 'meus-dados',
        meta: { breadcrumb: { label: 'Meus dados', to: { name: 'meus-dados' } } },
        component: () => import('./pages/profile'),
        children: [
          {
            path: '',
            name: 'meus-dados',
            component: () => import('./pages/profile/meus-dados'),
          },
          {
            path: 'alterar-dados',
            name: 'alterar-dados',
            meta: { breadcrumb: { label: 'Alterar dados' } },
            component: () => import('./pages/profile/meus-dados/alterar-dados'),
          },
          {
            path: 'alterar-endereco',
            name: 'alterar-endereco',
            meta: { breadcrumb: { label: 'Alterar endereço' } },
            component: () => import('./pages/profile/meus-dados/alterar-endereco'),
          },
          {
            path: 'alterar-senha',
            name: 'alterar-senha',
            meta: { breadcrumb: { label: 'Senha' } },
            component: () => import('./pages/profile/meus-dados/alterar-senha'),
          },
        ],
      },
      {
        path: 'minhas-compras',
        meta: { breadcrumb: { label: 'Minhas compras', to: { name: 'minhas-compras' } } },
        component: () => import('./pages/profile'),
        children: [
          {
            path: '',
            name: 'minhas-compras',
            component: () => import('./pages/profile/minhas-compras'),
          },
          {
            path: ':vertical/:orderNumber',
            name: 'pedido-detalhes',
            meta: { breadcrumb: route => [orderCrumb(route.params.vertical, route.params.orderNumber)] },
            component: () => import('./pages/profile/minhas-compras/pedido-detalhes'),
          },
          {
            // Subpedido de um pedido combinado. O número do pai vem na URL para o
            // detalhe do filho poder linkar de volta e apontar acessos/downloads
            // ao pai, sem precisar descobrir o número dele em outra chamada.
            path: ':vertical/:orderNumber/subpedido/:childOrderNumber',
            name: 'pedido-detalhes-subpedido',
            // O pedido pai não está em `route.matched` (esta rota é irmã, não filha
            // do detalhe), então este record rende os dois níveis.
            meta: {
              breadcrumb: route => [
                orderCrumb(route.params.vertical, route.params.orderNumber),
                {
                  label: `Pedido #${route.params.childOrderNumber}`,
                  to: { name: 'pedido-detalhes-subpedido', params: { ...route.params } },
                },
              ],
            },
            component: () => import('./pages/profile/minhas-compras/pedido-detalhes'),
          },
          {
            path: ':vertical/:orderNumber/acessos',
            name: 'order-accesses',
            meta: { breadcrumb: { label: 'Detalhes de acessos' } },
            component: () => import('./pages/profile/minhas-compras/acessos-segmentados'),
          },
        ],
      },
      {
        path: 'dados-pagamento',
        meta: { breadcrumb: { label: 'Dados de pagamento', to: { name: 'dados-pagamento' } } },
        component: () => import('./pages/profile'),
        children: [
          {
            path: '',
            name: 'dados-pagamento',
            component: () => import('./pages/profile/dados-pagamento'),
          },
          {
            path: 'cadastrar-cartao',
            name: 'cadastrar-cartao',
            meta: { breadcrumb: { label: 'Cadastrar novo cartão' } },
            component: () => import('./pages/profile/dados-pagamento/cadastrar-cartao'),
          },
        ],
      },
      {
        path: 'privacidade',
        meta: { breadcrumb: { label: 'Privacidade', to: { name: 'privacidade' } } },
        component: () => import('./pages/profile'),
        children: [
          {
            path: '',
            name: 'privacidade',
            component: () => import('./pages/profile/privacidade'),
          },
        ],
      },
    ],
  },

  // Auth Popup
  {
    path: '',
    component: () => import('./layouts/full-screen-centered'),
    children: [
      {
        path: '/auth-popup',
        name: 'auth-popup',
        meta: { stepperId: 'identificacao' },
        component: () => import('./pages/auth-popup'),
      },
    ],
  },

  // Login / Cadastro
  {
    path: '',
    component: () => import('./layouts/full-screen-centered-card'),
    beforeEnter: async (to, from, next) => {
      const sessionState = await validateSession({ scope: to.name });

      // se não tem token de sessão, continua normalmente pro login/cadastro
      if (sessionState === 'empty') {
        return next();
      }

      // 'invalid', 'error' ou status codes — limpa o cookie de sessão antes de poder entrar no login/cadastro
      if (sessionState !== 'valid') {
        const logoutTarget = window.location.origin + to.fullPath;
        return next({ name: 'logout', query: { target: logoutTarget } });
      }

      // sessionState === 'valid' garantido aqui.
      // Usuário já logado tentando acessar /login, /cadastro etc.:
      // honrar target se houver, senão cair no "/" (-> /area-do-aluno).
      const target = to.query.target;
      if (target) {
        return redirectToTarget({
          target,
          allowedHosts: Vue.env.VUE_APP_ALLOWED_TARGET_HOSTS.split(','),
          fallback: () => next('/'),
        });
      }

      return next('/');
    },
    children: [
      {
        path: '/login',
        name: 'login',
        meta: { stepperId: 'identificacao' },
        component: () => import('./pages/login'),
      },
      {
        path: '/enviar-chave-de-acesso',
        name: 'enviar-chave-de-acesso',
        meta: { stepperId: 'identificacao' },
        component: () => import('./pages/enviar-chave-de-acesso'),
      },
      {
        path: '/confirme-seu-codigo',
        name: 'confirme-seu-codigo',
        meta: { stepperId: 'identificacao' },
        component: () => import('./pages/confirme-seu-codigo'),
      },
      {
        path: '/login-confirme-seu-email',
        name: 'login-confirme-seu-email',
        meta: { stepperId: 'identificacao' },
        component: () => import('./pages/login-confirme-seu-email'),
      },
      {
        path: '/confirmacao-email',
        name: 'confirmacao-email',
        meta: { stepperId: 'identificacao' },
        component: () => import('./pages/confirmacao-email'),
      },
      {
        path: '/cadastro',
        name: 'cadastro',
        meta: { stepperId: 'identificacao' },
        component: () => import('./pages/cadastro'),
      },
      {
        path: '/cadastro-rapido',
        name: 'cadastro-rapido',
        meta: { stepperId: 'identificacao' },
        component: () => import('./pages/cadastro-rapido'),
      },
      {
        path: '/cadastro/preencher-email',
        name: 'cadastro-social-login-sem-email',
        meta: { stepperId: 'identificacao' },
        props: true,
        component: () => import('./pages/cadastro-social-login-sem-email'),
      },
      {
        path: '/cadastro-confirme-seu-email',
        name: 'cadastro-confirme-seu-email',
        meta: { stepperId: 'identificacao' },
        component: () => import('./pages/cadastro-confirme-seu-email'),
      },
      {
        path: '/cadastro-adiciona-senha',
        name: 'cadastro-atualiza-senha',
        meta: { stepperId: 'identificacao' },
        component: () => import('./pages/cadastro-adiciona-senha'),
      },
    ],
  },

  // Confirmação de email de comunicação
  {
    path: '',
    component: () => import('./layouts/full-screen-centered-card'),
    children: [
      {
        path: '/confirm-communication-email',
        name: 'confirm-communication-email',
        meta: { stepperId: 'identificacao' },
        component: () => import('./pages/confirm-communication-email'),
      },
      {
        path: '/confirm-communication-email-process',
        name: 'confirm-communication-email-process',
        meta: { stepperId: 'identificacao' },
        component: () => import('./pages/confirm-communication-email'),
      },
    ],
  },

  // Esqueci minha senha
  {
    path: '',
    component: () => import('./layouts/full-screen-centered-card'),
    beforeEnter: onlyWithoutTokenGuard,
    children: [
      {
        path: '/esqueci-senha',
        name: 'esqueci-senha',
        meta: { stepperId: 'identificacao' },
        component: () => import('./pages/esqueci-senha'),
      },
      {
        path: '/esqueci-senha-sucesso',
        name: 'esqueci-senha-sucesso',
        meta: { stepperId: 'identificacao' },
        component: () => import('./pages/esqueci-senha-sucesso'),
      },
    ],
  },

  // Recuperar senha
  {
    path: '',
    component: () => import('./layouts/with-header'),
    children: [
      {
        path: '/recuperar-senha',
        name: 'recuperar senha',
        meta: {
          headerTitle: 'Crie uma nova senha para entrar na Área do aluno e no app',
          headerIllustration: require('@/assets/images/header-illustration.svg'),
        },
        component: () => import('./pages/recuperar-senha'),
      },
    ],
  },
  /**
 * Rota para redirecionamento de callback de OAuth.
 * Foi necessário criar essa rota para que o callback de OAuth
 * Pois o window.location não funciona em rotas com o modo history.
 */
  {
    name: 'oauth-callback-redirect',
    path: '/oauth-callback-redirect',
    beforeEnter: async (to, from, next) => {
      const callbackURL = localStorage.getItem(ACCOUNTS_OAUTH_CALLBACK_URL);
      if (callbackURL) {
        localStorage.removeItem(ACCOUNTS_OAUTH_CALLBACK_URL);
        window.location = callbackURL;
        return next();
      }
    },
  },

  // Logout
  {
    path: '/sair',
    name: 'logout',
    component: () => import('./pages/logout'),
  },

  // Mobile captcha
  {
    path: '/mobile-captcha',
    name: 'mobile-captcha',
    component: () => import('./pages/mobile-captcha'),
  },

  // Catch all
  {
    path: '*',
    beforeEnter: async (to, from, next) => {
      const sessionState = await validateSession({ scope: 'catch-all' });

      if (sessionState === 'valid') {
        return next({ name: 'area-do-aluno' });
      }
      if (sessionState === 'empty') {
        return next({ name: 'login' });
      }
      return next({ name: 'logout' });
    },
  },
];

export const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
});
